import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LightBulbIcon, 
  NewspaperIcon, 
  ArrowPathIcon,
  BookOpenIcon,
  ShareIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import SmartImage from '../components/common/SmartImage';
import { resolveImageUrl } from '../utils/image';
import toast from 'react-hot-toast';

interface FactPost {
  id: string;
  type: 'fact' | 'news';
  title: string;
  content: string;
  image_url: string;
  source: string;
  source_url?: string;
  created_at: string;
}


const FactsPage: React.FC = () => {
  const [posts, setPosts] = useState<FactPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastPostElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    }, { rootMargin: '400px' });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const fetchPosts = async (pageNum: number) => {
    try {
      setLoading(true);
      const response = await api.get('/facts', {
        params: { page: pageNum, limit: 10 }
      });
      
      const newPosts = response.data.data.posts;
      setPosts(prev => {
        if (pageNum === 1) return newPosts;
        return [...prev, ...newPosts];
      });
      setHasMore(response.data.data.has_more);
      setError(null);
    } catch (err) {
      console.error('Error fetching facts:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(page);
  }, [page]);

  const handleShare = (post: FactPost) => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.content,
        url: post.source_url || window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(post.source_url || window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <header className="mb-12 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center px-4 py-1.5 rounded-full bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 font-bold text-xs uppercase tracking-widest mb-4 border border-teal-100 dark:border-teal-900/30"
        >
          <BookOpenIcon className="w-4 h-4 mr-2" />
          <span>Daily Insights</span>
        </motion.div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-2 tracking-tight">
          Photography & Modeling <span className="text-teal-500">Hub</span>
        </h1>
        <p className="text-gray-500 dark:text-gray-400 font-medium">
          Fresh facts and news from around the creative world.
        </p>
      </header>

      <div className="space-y-12">
        <AnimatePresence>
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index % 10 * 0.1 }}
              ref={index === posts.length - 1 ? lastPostElementRef : null}
              className="bg-white dark:bg-gray-900 rounded-[2.5rem] overflow-hidden border border-gray-100 dark:border-gray-800 shadow-2xl shadow-gray-200/50 dark:shadow-none hover:shadow-teal-500/10 transition-all duration-500 group"
            >
              {/* Post Header */}
              <div className="px-8 pt-8 pb-6 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2.5 rounded-2xl ${
                    post.type === 'fact' 
                      ? 'bg-amber-50 text-amber-500 dark:bg-amber-900/20' 
                      : 'bg-blue-50 text-blue-500 dark:bg-blue-900/20'
                  }`}>
                    {post.type === 'fact' ? <LightBulbIcon className="w-6 h-6" /> : <NewspaperIcon className="w-6 h-6" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white leading-tight">
                      {post.type === 'fact' ? 'Interesting Fact' : 'Industry News'}
                    </h3>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                      {post.source} • {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => handleShare(post)}
                  className="p-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-400 hover:text-teal-500 transition-colors"
                >
                  <ShareIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Related Image (Moved Up) */}
              {post.image_url && (
                <div className="px-4 pb-6">
                  <div className="relative aspect-[16/10] overflow-hidden rounded-[2.5rem]">
                    <SmartImage 
                      src={post.image_url} 
                      alt={post.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none"
                      withWatermark={true}
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
                  </div>
                </div>
              )}

              {/* Title & Content (Now below image) */}
              <div className="px-8 pb-8">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-4 leading-tight group-hover:text-teal-500 transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                  {post.content.includes('[Read More]') ? (
                    <>
                      {post.content.split('[Read More]')[0]}
                      <a 
                        href={post.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-500 hover:text-teal-600 font-bold ml-1 inline-flex items-center"
                      >
                        [Read More]
                      </a>
                    </>
                  ) : (
                    post.content
                  )}
                </p>
              </div>

              {/* Footer */}
              {post.source_url && (
                <div className="px-8 pb-8">
                  <a 
                    href={post.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-black text-sm hover:bg-teal-500 hover:text-white dark:hover:bg-teal-500 transition-all group/btn"
                  >
                    <span>Read Full Article</span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                  </a>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mb-4" />
          <p className="text-gray-400 font-black text-xs uppercase tracking-widest">Updating Feed</p>
        </div>
      )}

      {!hasMore && !loading && posts.length === 0 && (
        <div className="text-center py-20">
          <div className="inline-flex items-center space-x-2 text-gray-300 dark:text-gray-700 font-black uppercase text-xs tracking-widest">
            <div className="h-px w-12 bg-gray-200 dark:bg-gray-800" />
            <span>No results found</span>
            <div className="h-px w-12 bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-10 bg-red-50 dark:bg-red-900/10 rounded-3xl border border-red-100 dark:border-red-900/20 p-8">
          <p className="text-red-600 dark:text-red-400 font-bold mb-4">{error}</p>
          <button 
            onClick={() => fetchPosts(page)}
            className="px-8 py-3 bg-red-600 text-white rounded-2xl font-black hover:bg-red-700 shadow-lg shadow-red-200"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default FactsPage;
